import styled from 'styled-components';

import InputContainer from '../Input';

import { COLORS } from '../../../helpers/constants/styleguide';

/**
 * @typedef {Object} TextAreaInputOwnProps
 * @property {string | number} [value]
 * @property {(event: React.ChangeEvent<HTMLTextAreaElement>) => void} [onChange]
 * @property {(event: React.FocusEvent<HTMLTextAreaElement>) => void} [onBlur]
 * @property {(event: React.KeyboardEvent<HTMLTextAreaElement>) => void} [onKeyPress]
 * @property {string} [placeholder]
 * @property {number} [maxLength]
 * @property {boolean} [disabled]
 * @property {boolean} [readOnly]
 * @property {string} [autoComplete]
 */

/** @typedef {import('../Input').InputFieldProps} InputFieldProps */
/** @typedef {InputFieldProps & TextAreaInputOwnProps} TextAreaInputProps */

/** @type {React.FC<TextAreaInputProps>} */
const TextAreaInput = ({
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  placeholder = '',
  // eslint-disable-next-line react/prop-types -- pre-existing; surfaced by reflow from a type-only change
  onChange,
  value,
  name,
  maxLength,
  height = '80px',
  disabled,
  validate,
  autoComplete,
  readOnly,
  onKeyPress,
  error,
  touched,
  isSubmitting,
  label,
  helpMessage,
  onBlur = () => {},
}) => {
  const renderInputText = (handleFocusChange, focus, { inputId, labelId }) => (
    <Input
      id={inputId}
      aria-labelledby={labelId}
      placeholder={placeholder}
      name={name}
      height={height}
      validate={validate}
      maxLength={maxLength}
      value={value}
      onChange={onChange}
      onFocus={handleFocusChange}
      disabled={disabled}
      autoComplete={autoComplete}
      readOnly={readOnly}
      onKeyPress={onKeyPress}
      onBlur={e => {
        handleFocusChange();
        onBlur(e);
      }}
    />
  );

  return (
    <InputContainer
      id={name}
      error={error}
      touched={touched}
      height={height}
      isSubmitting={isSubmitting}
      label={label}
      helpMessage={helpMessage}
    >
      {renderInputText}
    </InputContainer>
  );
};

const Input = styled.textarea`
  width: 100%;
  height: 100%;
  font-size: 16px;
  font-weight: 300;
  vertical-align: top;
  text-wrap: wrap;
  background-color: transparent;
  border: none;
  caret-color: ${COLORS.darkerGrey};
  color: ${COLORS.darkerGrey};
  padding: 16px;

  ${({ resize }) => `
  resize: ${resize ?? 'none'};
  `}

  &:focus {
    outline: none;
  }

  ::placeholder {
    color: ${COLORS.medGrey};
    opacity: 1; /* Firefox */
  }
`;

export default TextAreaInput;
